import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qaqcApi, type PourCard, type Material } from '@/lib/apiClient';
import { Combobox } from '@/components';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { POUR_TYPE_LABEL, fmtDt, fmtD, fmtDTm, fmtWallD, bpoLabel, PourAction, useJobPicker, isRock, qtyDisplay } from './pourShared';

// The full Pour Card drawer (mock 13: status timeline + office action +
// procurement link + pour details + read-only Formstack checklist). Shared
// between the per-project QA/QC tab and the company-wide roll-up page — the
// PEs manage mostly from the roll-up, so the detail must be identical there.

// ─── Pour Card drawer (mock 13: timeline + details + checklist) ──────────────
const TIMELINE = [
  { key: 'draft',     title: 'Field Draft',            sub: 'Started on the Formstack pre-pour form' },
  { key: 'pending',   title: 'Pending Office Confirm', sub: 'Field submitted the form — synced via webhook' },
  { key: 'confirmed', title: 'Order Confirmed',        sub: 'Office confirms with the vendor & field leader' },
  { key: 'poured',    title: 'Poured',                 sub: 'Marked here — the Daily Log link automates this soon' },
];
// An office-entered card (+ Add order) never touched Formstack, so the first
// two steps say what actually happened instead.
const MANUAL_SUB: Record<string, string> = {
  draft:   'No form — the order came to the office directly',
  pending: 'Entered by the office in the Project Tracker',
};
// A rock order comes from its own form and is delivered, not poured.
const ROCK_STEP: Record<string, { title?: string; sub: string }> = {
  draft:  { sub: 'Started on the Formstack Rock/Aggregate form' },
  poured: { title: 'Delivered', sub: 'Marked here once the load is on site' },
};
const STATUS_RANK: Record<string, number> = { pending: 1, confirmed: 2, poured: 3, cancelled: 1, covered: 2 };

/** Stable display label for a Materials line in the commitment picker. */
function materialLabel(m: Material): string {
  return m.sagePoNumber ? `${m.item} (${m.sagePoNumber})` : m.item;
}

export default function PourCardDrawer({ card, jobNumber, canEdit, materials, onClose }: {
  card: PourCard; jobNumber: string; canEdit: boolean;
  materials: Material[]; onClose: () => void;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });
  const qc = useQueryClient();
  const rank = STATUS_RANK[card.status] ?? 1;
  const manual = card.source === 'manual';
  const rock = isRock(card);
  const timeline = rock ? TIMELINE.map((s) => ({ ...s, ...ROCK_STEP[s.key] })) : TIMELINE;

  // "Assign to job" — office fix for a typo'd job number on the form. The
  // picker loads the unscoped project list lazily (only when unmatched, or
  // when the user asks to reassign).
  const [reassigning, setReassigning] = useState(false);
  const [jobText, setJobText] = useState('');
  const needsJob = card.projectId == null;
  const jobPicker = useJobPicker(needsJob || reassigning);
  const assignMut = useMutation({
    mutationFn: (projectId: number) => qaqcApi.assignProject(card.id, projectId),
    onSuccess: () => {
      setReassigning(false);
      setJobText('');
      qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
      qc.invalidateQueries({ queryKey: ['project-counts'] });
    },
  });

  // Commitment picker (typed by item name; free text doesn't link).
  const [linkText, setLinkText] = useState('');
  const linkMut = useMutation({
    mutationFn: (materialId: number | null) => qaqcApi.linkMaterial(card.id, materialId),
    onSuccess: () => {
      setLinkText('');
      qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
      qc.invalidateQueries({ queryKey: ['materials'] });
    },
  });
  const materialByLabel = useMemo(() => {
    const m = new Map<string, Material>();
    for (const mat of materials) m.set(materialLabel(mat), mat);
    return m;
  }, [materials]);

  // Read-back rows (PE ask): the office confirms orders over the phone with
  // the vendor and foreman, so values are set big and bold to scan mid-call.
  const detail = (label: string, value: string | null) => <DetailRow label={label} value={value} />;

  // Fields the parser doesn't column-ize live in the stored checklist JSON —
  // matched by label so a Formstack rewording degrades to '—', never crashes.
  const chk = (re: RegExp) => card.checklist.find((it) => re.test(it.label ?? ''))?.value ?? null;

  // ── Office typo-fix (PE ask): Edit toggles the read-back rows into inputs;
  // every changed field is audited server-side and shows in Activity below. ──
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [editError, setEditError] = useState<string | null>(null);
  const startEdit = () => {
    setForm({
      pourType: card.pourType,
      element: card.element ?? '',
      location: card.location ?? '',
      // Wall-clock digits: the stored UTC parts ARE the form's local time.
      pourDatetime: card.pourDatetime ? new Date(card.pourDatetime).toISOString().slice(0, 16) : '',
      mix: card.mix ?? '',
      qtyText: card.qtyText ?? '',
      vendor: card.vendor ?? '',
      costCode: card.costCode ?? '',
      placement: card.placement ?? '',
      superName: card.superName ?? '',
      slump: chk(/slump/i) ?? '',
      truckSpacing: chk(/truck\s*spacing/i) ?? '',
      admixtures: chk(/admixture/i) ?? '',
    });
    setEditError(null);
    setEditing(true);
  };
  const saveMut = useMutation({
    mutationFn: () => {
      const dto: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(form)) {
        if (k === 'pourDatetime') { dto[k] = v ? `${v}:00.000Z` : null; continue; }
        dto[k] = v.trim() === '' ? null : v.trim();
      }
      return qaqcApi.updatePourCard(card.id, dto);
    },
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
      qc.invalidateQueries({ queryKey: ['qaqc-card-edits', card.id] });
    },
    onError: (e: any) => setEditError(e?.message ?? 'Failed to save'),
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));
  const inputCss: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '5px 8px',
    font: 'var(--ew-fw-regular) 13px var(--ew-font)', width: 220, maxWidth: '60%',
    textAlign: 'right', background: '#fff', color: 'var(--ew-dark)',
  };
  const editRow = (label: string, key: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '5px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 13 }}>
      <span style={{ color: 'var(--ew-n-500)', flexShrink: 0 }}>{label}</span>
      {key === 'pourType' ? (
        // A will-call confirmed with the main pour keeps its type (the API
        // refuses the change): 'covered' only exists for will-calls.
        <select style={inputCss} value={form.pourType} onChange={set('pourType')}
                disabled={card.status === 'covered'}
                title={card.status === 'covered' ? 'Confirmed with the main pour — the type can no longer change' : undefined}>
          {Object.entries(POUR_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : key === 'pourDatetime' ? (
        <input style={inputCss} type="datetime-local" value={form.pourDatetime} onChange={set('pourDatetime')} />
      ) : (
        <input style={inputCss} value={form[key] ?? ''} onChange={set(key)} />
      )}
    </div>
  );

  // ── Order confirmation notes: its own edit toggle, separate from Pour
  // Details, because the two are different jobs. Pour Details is fixing what
  // the FIELD typed; this is recording what the OFFICE did. Same audited
  // endpoint, so both show up in Activity below.
  //
  // Unlike Pour Details, this stays editable on a poured card — the PE often
  // writes up the call after the concrete is down, and a locked box would just
  // push the note into someone's inbox instead. Only cancelled orders close it.
  const canEditNotes = canEdit && card.status !== 'cancelled';
  const hasNotes = Boolean(card.dispatcherName || card.dispatcherPhone || card.orderNotes);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesForm, setNotesForm] = useState<Record<string, string>>({});
  const [notesError, setNotesError] = useState<string | null>(null);
  const startNotes = () => {
    setNotesForm({
      dispatcherName: card.dispatcherName ?? '',
      dispatcherPhone: card.dispatcherPhone ?? '',
      orderNotes: card.orderNotes ?? '',
    });
    setNotesError(null);
    setNotesEditing(true);
  };
  const notesMut = useMutation({
    mutationFn: () => {
      const dto: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(notesForm)) dto[k] = v.trim() === '' ? null : v.trim();
      return qaqcApi.updatePourCard(card.id, dto);
    },
    onSuccess: () => {
      setNotesEditing(false);
      qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
      qc.invalidateQueries({ queryKey: ['qaqc-card-edits', card.id] });
    },
    onError: (e: any) => setNotesError(e?.message ?? 'Failed to save'),
  });
  const noteRow = (label: string, key: string, placeholder: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '5px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 13 }}>
      <span style={{ color: 'var(--ew-n-500)', flexShrink: 0 }}>{label}</span>
      <input style={inputCss} placeholder={placeholder} value={notesForm[key] ?? ''}
             onChange={(e) => setNotesForm((p) => ({ ...p, [key]: e.target.value }))} />
    </div>
  );

  // Activity stream: audited field edits + the lifecycle stamps already on
  // the card, merged newest-first.
  const editsQuery = useQuery({
    queryKey: ['qaqc-card-edits', card.id],
    queryFn: () => qaqcApi.pourCardEdits(card.id),
    staleTime: 30_000,
  });
  const activity = useMemo(() => {
    const ev: { at: string; text: string }[] = [];
    ev.push({ at: card.createdAt, text: manual
      ? `Entered by the office${card.createdBy ? ` (${card.createdBy})` : ''} — no ${rock ? 'Rock/Aggregate form' : 'Pre-Pour Checklist'}`
      : 'Submitted from the field (Formstack)' });
    if (card.confirmedAt) ev.push({ at: card.confirmedAt, text: `Order confirmed${card.confirmedBy ? ` by ${card.confirmedBy}` : ''}` });
    // Same status as a pour underneath; a rock order is delivered, like the
    // timeline and status strip above say (Codex on #582).
    if (card.pouredAt) ev.push({ at: card.pouredAt, text: `${rock ? 'Marked delivered' : 'Marked poured'}${card.pouredBy ? ` by ${card.pouredBy}` : ''}${card.pouredSource === 'daily-log' ? ' (via Daily Log)' : ''}` });
    if (card.cancelledAt) ev.push({ at: card.cancelledAt, text: `Order cancelled${card.cancelledBy ? ` by ${card.cancelledBy}` : ''}` });
    if (card.coveredAt) ev.push({ at: card.coveredAt, text: `Confirmed with the main pour${card.coveredBy ? ` by ${card.coveredBy}` : ''}` });
    for (const e of editsQuery.data ?? []) {
      ev.push({ at: e.editedAt, text: `${e.editedBy ?? 'Someone'} changed ${e.field}: "${e.oldValue ?? '—'}" → "${e.newValue ?? '—'}"` });
    }
    return ev.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [card, editsQuery.data]);

  const bySection = card.checklist.reduce<Record<string, typeof card.checklist>>((acc, it) => {
    (acc[it.section || 'Details'] ??= []).push(it);
    return acc;
  }, {});

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(54,54,54,0.32)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ew-n-100)', position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>{rock ? 'Pour Card · Rock / Aggregate Order' : 'Pour Card · Concrete Order'}</div>
            <div style={{ font: 'var(--ew-fw-semibold) 17px/1.3 var(--ew-font)', margin: '6px 0 2px' }}>{card.element ?? (rock ? 'Rock / Aggregate' : 'Pour')}</div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>{[rock ? card.vendor : card.mix, qtyDisplay(card)].filter(Boolean).join(' · ') || '—'}</div>
            <div style={{ fontSize: 11, color: 'var(--ew-n-400)', marginTop: 2 }}>
              {manual ? `Entered by the office${card.createdBy ? ` (${card.createdBy})` : ''}` : 'Submitted from the field'} {fmtDTm(card.createdAt)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '4px 20px 32px' }}>
          {/* ── Job assignment (typo'd job numbers get fixed here) ── */}
          {(needsJob || reassigning) ? (
            <div style={{ margin: '16px 0 4px' }}>
              <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-safety-700)', marginBottom: 8 }}>
                {needsJob ? 'No matching job' : 'Reassign job'}
              </div>
              {needsJob && (
                <div style={{ background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-safety-700)', marginBottom: 8 }}>
                  The form entered job “{card.jobNumberRaw ?? '—'}”, which doesn't match any project — likely a typo. Assign the right job below.
                </div>
              )}
              {canEdit ? (
                <>
                  <Combobox
                    value={jobText}
                    onChange={(v) => {
                      setJobText(v);
                      const pid = jobPicker.idByLabel.get(v);
                      if (pid) assignMut.mutate(pid);
                    }}
                    options={jobPicker.options}
                    placeholder="Type a job # or name…"
                  />
                  {reassigning && (
                    <button onClick={() => { setReassigning(false); setJobText(''); }} style={{ border: 'none', background: 'none', color: 'var(--ew-n-500)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', marginTop: 4 }}>
                      Cancel reassign
                    </button>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--ew-n-400)' }}>A project editor can assign this card to the right job.</div>
              )}
            </div>
          ) : (
            <div style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--ew-n-500)', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span>Job <b style={{ color: 'var(--ew-dark)' }}>#{jobNumber}</b></span>
              {card.jobNumberRaw && card.project && card.jobNumberRaw !== card.project.jobNumber && (
                <span style={{ color: 'var(--ew-safety-700)' }}>(form entered “{card.jobNumberRaw}”)</span>
              )}
              {canEdit && (
                <button onClick={() => setReassigning(true)} style={{ border: 'none', background: 'none', color: 'var(--ew-n-400)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
                  reassign
                </button>
              )}
            </div>
          )}

          {/* ── Order status timeline ── */}
          <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '18px 0 12px' }}>Order Status</div>
          {timeline.map((step, i) => {
            const stepRank = i; // draft=0 pending=1 confirmed=2 poured=3
            const done = stepRank < rank;
            const current = stepRank === rank;
            return (
              <div key={step.key} style={{ display: 'flex', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: 999, flexShrink: 0, marginTop: 3,
                    background: done ? 'var(--ew-green)' : current ? '#fff' : '#fff',
                    border: done ? 'none' : current ? '3px solid var(--ew-green)' : '2px solid var(--ew-n-300)',
                    boxSizing: 'border-box',
                  }} />
                  {i < timeline.length - 1 &&<span style={{ width: 2, flex: 1, minHeight: 22, background: done ? 'var(--ew-green)' : 'var(--ew-n-200)' }} />}
                </div>
                <div style={{ paddingBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: done || current ? 'var(--ew-dark)' : 'var(--ew-n-400)', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {step.title}
                    {current && <span style={{ font: 'var(--ew-fw-semibold) 10px/1.6 var(--ew-font)', background: 'var(--ew-green-50)', color: 'var(--ew-green-700)', borderRadius: 'var(--ew-r-pill)', padding: '1px 8px' }}>Current</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginTop: 2 }}>{(manual && MANUAL_SUB[step.key]) || step.sub}</div>
                </div>
              </div>
            );
          })}

          {canEdit && card.status !== 'poured' && card.status !== 'cancelled' && card.status !== 'covered' && (
            <div style={{ margin: '4px 0 8px' }}>
              <PourAction card={card} jobNumber={jobNumber} />
              {card.status === 'confirmed' && card.pourType !== 'willcall' && !rock && (
                <div style={{ fontSize: 11, color: 'var(--ew-n-400)', marginTop: 6 }}>Recording the pour on that day's Daily Log also flips this automatically.</div>
              )}
            </div>
          )}
          {card.status === 'cancelled' && (
            <div style={{ background: 'var(--ew-n-100)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-n-500)', margin: '10px 0' }}>
              Cancelled{card.cancelledAt ? ` on ${fmtD(card.cancelledAt)}` : ''}{card.cancelledBy ? ` by ${card.cancelledBy}` : ''} — typically superseded by a new Main Pour form or called off.
            </div>
          )}
          {card.status === 'covered' && (
            <div style={{ background: 'var(--ew-green-50)', border: '1px solid var(--ew-green-100, #cfe6d5)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-green-700)', margin: '10px 0' }}>
              Confirmed with the main pour{card.coveredAt ? ` on ${fmtD(card.coveredAt)}` : ''}{card.coveredBy ? ` by ${card.coveredBy}` : ''} — the concrete came in with the main pour, so this will-call is off the schedule.
            </div>
          )}

          {/* ── Order confirmation notes (product owner, 2026-07-29) — the record of the
               phone call, sat directly above the details the PE reads OFF that
               call. Editable at any open status, because the call happens
               BEFORE "Confirm order" is clicked, not after; and still readable
               (not editable) once poured/cancelled so the history survives. ── */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '18px 0 2px' }}>
            <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)' }}>Order Confirmation Notes</span>
            {canEditNotes && !notesEditing ? (
              <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={startNotes}>
                {hasNotes ? 'Edit' : 'Add'}
              </button>
            ) : null}
          </div>
          <div style={{ font: 'var(--ew-fw-regular) 11px/1.4 var(--ew-font)', color: 'var(--ew-n-400)', marginBottom: 4 }}>
            Who you reached at the plant — this goes into the vendor confirmation email
          </div>
          {notesEditing ? (
            <>
              {noteRow('Dispatcher', 'dispatcherName', 'Name of the person you spoke with')}
              {noteRow('Dispatch phone', 'dispatcherPhone', 'Direct number that reached them')}
              <div style={{ padding: '5px 0', fontSize: 13 }}>
                <div style={{ color: 'var(--ew-n-500)', marginBottom: 4 }}>Call notes</div>
                <textarea
                  value={notesForm.orderNotes ?? ''}
                  onChange={(e) => setNotesForm((p) => ({ ...p, orderNotes: e.target.value }))}
                  maxLength={2000}
                  rows={4}
                  placeholder="What was agreed on the call — time changes, load splits, will-call standby, who calls whom…"
                  style={{ ...inputCss, width: '100%', maxWidth: '100%', textAlign: 'left', resize: 'vertical', font: 'var(--ew-fw-regular) 13px/1.45 var(--ew-font)' }}
                />
                <div style={{ fontSize: 10.5, color: 'var(--ew-n-400)', textAlign: 'right' }}>{(notesForm.orderNotes ?? '').length} / 2000</div>
              </div>
              {notesError ? <div style={{ margin: '8px 0 0', padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{notesError}</div> : null}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button className="ew-btn-outline" onClick={() => setNotesEditing(false)} disabled={notesMut.isPending}>Cancel</button>
                <button className="ew-btn-outline" style={{ background: 'var(--ew-green)', borderColor: 'var(--ew-green)', color: '#fff' }} onClick={() => notesMut.mutate()} disabled={notesMut.isPending}>
                  {notesMut.isPending ? 'Saving…' : 'Save notes'}
                </button>
              </div>
            </>
          ) : hasNotes ? (
            <>
              {detail('Dispatcher', card.dispatcherName)}
              {detail('Dispatch phone', card.dispatcherPhone)}
              {card.orderNotes ? (
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 13 }}>
                  <div style={{ color: 'var(--ew-n-500)', marginBottom: 3 }}>Call notes</div>
                  <div style={{ whiteSpace: 'pre-wrap', color: 'var(--ew-dark)', lineHeight: 1.45 }}>{card.orderNotes}</div>
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--ew-n-400)', padding: '6px 0 2px' }}>
              Nothing logged yet{canEditNotes ? ' — add the dispatcher and what was agreed once you’ve called.' : '.'}
            </div>
          )}

          {/* ── Pour details — ordered for confirmation-call read-back (the
               PEs' list, verbatim). Slump / truck spacing / admixtures /
               phone come from the stored checklist; address + PM from the
               linked project; PO # from the linked Materials commitment. ── */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '18px 0 2px' }}>
            <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)' }}>Pour Details</span>
            {canEdit && !editing ? (
              <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={startEdit}>Edit</button>
            ) : null}
          </div>
          <div style={{ font: 'var(--ew-fw-regular) 11px/1.4 var(--ew-font)', color: 'var(--ew-n-400)', marginBottom: 4 }}>
            {editing ? 'Fix any field-entered typos — every change is logged in Activity below' : 'Everything you need on the phone with the vendor & foreman'}
          </div>
          {editing ? (
            <>
              {editRow('Order type', 'pourType')}
              {/* The type being picked, not the saved one: switching to or
                  from rock swaps the fields straight away. */}
              {form.pourType === 'rock' ? (
                <>
                  {editRow('Type of aggregate', 'element')}
                  {editRow('Delivery location', 'location')}
                  {editRow('Supplier', 'vendor')}
                  {editRow('Delivery date', 'pourDatetime')}
                  {editRow('Quantity (with unit)', 'qtyText')}
                  {editRow('Field Leader', 'superName')}
                  {editRow('Cost code', 'costCode')}
                </>
              ) : (
                <>
                  {editRow('Element', 'element')}
                  {editRow('Location', 'location')}
                  {editRow('Vendor', 'vendor')}
                  {editRow('Pour date', 'pourDatetime')}
                  {editRow('Mix', 'mix')}
                  {editRow('Quantity (CY)', 'qtyText')}
                  {editRow('Slump', 'slump')}
                  {editRow('Truck spacing', 'truckSpacing')}
                  {editRow('Admixtures', 'admixtures')}
                  {editRow('Field Leader', 'superName')}
                  {editRow('Cost code', 'costCode')}
                  {editRow('Placement', 'placement')}
                </>
              )}
              {editError ? <div style={{ margin: '8px 0 0', padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{editError}</div> : null}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button className="ew-btn-outline" onClick={() => setEditing(false)} disabled={saveMut.isPending}>Cancel</button>
                <button className="ew-btn-outline" style={{ background: 'var(--ew-green)', borderColor: 'var(--ew-green)', color: '#fff' }} onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </>
          ) : rock ? (
            <>
              {detail('Order type', POUR_TYPE_LABEL[card.pourType] ?? card.pourType)}
              {detail('Supplier', card.vendor)}
              {detail('Delivery date', fmtDt(card.pourDatetime))}
              {detail('Type of aggregate', card.element)}
              {detail('Quantity', qtyDisplay(card))}
              {detail('Delivery location', card.location)}
              {detail('Field Leader', card.superName)}
              <FieldLeaderPhone card={card} formPhone={chk(/(super|foreman).*(phone|number)|phone\s*number/i)} />
              {detail('Job address', card.project?.jobAddress ?? null)}
              {detail('Blanket PO', bpoLabel(card.project))}
              {detail('PO # (this order)', card.materialSagePo)}
              {detail('Cost code', card.costCode)}
              {detail('PM', card.project?.pmName ?? card.pmEmail)}
            </>
          ) : (
            <>
              {detail('Pour type', POUR_TYPE_LABEL[card.pourType] ?? card.pourType)}
              {detail('Vendor', card.vendor)}
              {detail('Pour date', fmtDt(card.pourDatetime))}
              {detail('Mix / quantity', [card.mix, card.qtyText ? `${card.qtyText} CY` : null].filter(Boolean).join(' · ') || null)}
              <MixCallout card={card} />
              {card.mixMatch === 'approved' && card.mixRetired && (
                <div style={{ margin: '6px 0 2px', fontSize: 11.5, color: 'var(--ew-n-500)', lineHeight: 1.45 }}>
                  Approved against <b>{card.approvedMixId}</b>, a mix since retired on this job — the match is kept as a record
                  of what was approved at the time.
                </div>
              )}
              {detail('Slump', chk(/slump/i))}
              {detail('Truck spacing', chk(/truck\s*spacing/i))}
              {detail('Admixtures', chk(/admixture/i))}
              {detail('Field Leader', card.superName)}
              <FieldLeaderPhone card={card} formPhone={chk(/(super|foreman).*(phone|number)|phone\s*number/i)} />
              {detail('Job address', card.project?.jobAddress ?? null)}
              {/* The job's standing concrete buy — supplier and blanket PO. The
                  office reads these off their sheet before dialling, so they sit
                  with the other read-back rows rather than under Procurement. */}
              {detail('Supplier (job)', card.project?.concreteSupplier ?? null)}
              {detail('Blanket PO', bpoLabel(card.project))}
              {detail('PO # (this pour)', card.materialSagePo)}
              {detail('Cost code', card.costCode)}
              {detail('PM', card.project?.pmName ?? card.pmEmail)}
              {detail('Placement', card.placement)}
              {card.project && !card.project.concreteSupplier && !card.project.concreteBpo && !card.project.aggregateBpo && (
                <div style={{ margin: '8px 0 2px', fontSize: 11.5, color: 'var(--ew-n-500)', lineHeight: 1.45 }}>
                  No supplier or blanket PO recorded for this job — set them under{' '}
                  <b>Concrete Ordering</b> on the job's QA/QC tab. The estimator raises the blanket PO; the
                  supplier may be theirs or the PM's depending on the job. They'll then show here and on the pour schedule.
                </div>
              )}
            </>
          )}

          {/* ── Procurement: link to the Materials concrete commitment ── */}
          <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '18px 0 8px' }}>Procurement</div>
          {card.materialId ? (
            <div style={{ background: 'var(--ew-quality-50)', border: '1px solid var(--ew-quality-100)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-quality-700)', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <span>
                Released against Materials → <b>{card.materialItem}</b>{card.materialSagePo ? ` · ${card.materialSagePo}` : ''}.{rock ? '' : ' CY rolls into the commitment.'}
              </span>
              {canEdit && (
                <button onClick={() => linkMut.mutate(null)} style={{ border: 'none', background: 'none', color: 'var(--ew-quality-700)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                  Unlink
                </button>
              )}
            </div>
          ) : canEdit ? (
            <div>
              <Combobox
                value={linkText}
                onChange={(v) => {
                  setLinkText(v);
                  const hit = materialByLabel.get(v);
                  if (hit) linkMut.mutate(hit.id);
                }}
                options={materials.map((m) => ({ value: materialLabel(m), sub: [m.vendor, m.costCode, m.qty].filter(Boolean).join(' · ') || undefined }))}
                placeholder="Link to a Materials commitment (PO)…"
              />
              <div style={{ fontSize: 11, color: 'var(--ew-n-400)', marginTop: 4 }}>
                {rock
                  ? 'Pick the aggregate commitment this delivery releases against — its Sage PO lives on the Materials tab.'
                  : 'Pick the concrete commitment this pour releases against — its Sage PO and the running CY total live on the Materials tab.'}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--ew-n-400)' }}>Not linked to a Materials commitment yet.</div>
          )}

          {/* ── Status strips ── */}
          {card.status === 'confirmed' && (
            <div style={{ background: 'var(--ew-green-50)', border: '1px solid var(--ew-green-100)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-green-700)', margin: '10px 0' }}>
              Order confirmed by {card.confirmedBy ?? 'the office'}{card.vendor ? ` with ${card.vendor}` : ''}{card.superName ? ` and field leader ${card.superName}` : ''}.
            </div>
          )}
          {card.status === 'poured' && (
            <div style={{ background: 'var(--ew-quality-50)', border: '1px solid var(--ew-quality-100)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-quality-700)', margin: '10px 0' }}>
              {rock ? 'Delivered' : 'Poured'} — marked by {card.pouredBy ?? 'the office'} on {fmtD(card.pouredAt)}.
            </div>
          )}
          {/* Concrete only: a card switched to rock keeps its report (so a switch
              back restores it), but it says nothing about a stone delivery. */}
          {card.actualPourAt && !rock && (
            <div style={{ background: 'var(--ew-n-50, #FAFAFA)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '8px 12px', fontSize: 12, color: 'var(--ew-n-600)', margin: '10px 0' }}>
              Final Quantity Report: {card.actualQtyCy != null ? `${card.actualQtyCy} CY` : 'quantity n/a'} · {fmtWallD(card.actualPourAt)}{card.actualNotes ? ` — ${card.actualNotes}` : ''}
            </div>
          )}

          {/* ── Checklist (read-only, from Formstack) ── */}
          <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '22px 0 8px' }}>{rock ? 'Rock/Aggregate form · other answers' : 'Pre-Pour Checklist · from Formstack'}</div>
          {card.checklist.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
              {card.source === 'manual'
                ? `None — this order was entered by the office, so no ${rock ? 'Rock/Aggregate form' : 'Pre-Pour Checklist'} was submitted for it.`
                : rock
                ? 'Nothing else — every answer on the form is shown in the details above.'
                : 'No checklist was captured with this card (it predates the webhook capture or was created by hand).'}
            </div>
          )}
          {Object.entries(bySection).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 12 }}>
              {section !== 'Details' && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ew-n-500)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '8px 0 4px' }}>{section}</div>}
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--ew-n-100)' }}>
                  <span style={{ color: 'var(--ew-n-600)' }}>{it.label}</span>
                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: it.value.toLowerCase() === 'no' ? 'var(--ew-danger)' : 'var(--ew-dark)' }}>
                    {/^https?:\/\//.test(it.value)
                      ? <a href={it.value} target="_blank" rel="noreferrer" style={{ color: 'var(--ew-quality)' }}>attachment ↗</a>
                      : it.value}
                  </span>
                </div>
              ))}
            </div>
          ))}

          {/* ── Activity — audited corrections + lifecycle stamps, newest first ── */}
          <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ew-green)', margin: '22px 0 8px' }}>Activity</div>
          {activity.map((ev, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 12 }}>
              <span style={{ color: 'var(--ew-n-400)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmtDTm(ev.at)}</span>
              <span style={{ color: 'var(--ew-n-600)' }}>{ev.text}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/** One read-back row. `children` replaces the value when a row needs more than
 *  a string (the phone row carries its provenance). */
function DetailRow({ label, value, children }: {
  label: string; value?: string | null; children?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 13 }}>
      <span style={{ color: 'var(--ew-n-500)', flexShrink: 0 }}>{label}</span>
      {children ?? (
        <span style={{ textAlign: 'right', fontWeight: 600, fontSize: 13.5, color: value ? 'var(--ew-dark)' : 'var(--ew-n-400)' }}>{value || '—'}</span>
      )}
    </div>
  );
}

/**
 * The field leader's number, WITH where it came from.
 *
 * The office rang this number off an exact name match until 2026-07-31, and a
 * single mistyped character silently blanked it. Resolution is now a ladder
 * (see the API's lib/fieldContact), which means the number on screen may belong
 * to a corrected spelling or to the job's assigned leader rather than the name
 * the field typed.
 *
 * So the provenance is never optional. A bare number the office can't attribute
 * is worse than no number — it's a call to the wrong foreman with no way to tell.
 * Only an exact match renders as a plain number.
 */
function FieldLeaderPhone({ card, formPhone }: { card: PourCard; formPhone: string | null }) {
  // The form itself occasionally carries a phone answer. It's last: it's the
  // same free-typed source as the name, with no directory to check it against.
  const phone = card.superPhone ?? formPhone;
  const note = card.superPhoneSource === 'close-match'
    ? `“${card.superName}” isn’t in the field directory — this is ${card.superPhoneName}, the closest name on record. Check it’s the right person, and fix the name with Edit above.`
    : card.superPhoneSource === 'job-leader'
      ? `Not resolved from “${card.superName ?? '—'}” — this is ${card.superPhoneName}, the field leader assigned to this job.`
      : card.superPhone == null && formPhone
        ? 'Taken from the form’s own phone answer — not checked against the directory.'
        : null;
  const unresolved = phone == null && (card.superName ?? '').trim() !== '';

  return (
    <>
      <DetailRow label="Field Leader's number">
        <span style={{ textAlign: 'right', fontWeight: 600, fontSize: 13.5, color: phone ? 'var(--ew-dark)' : 'var(--ew-n-400)' }}>
          {phone ? <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>{phone}</a> : '—'}
        </span>
      </DetailRow>
      {note && (
        <div style={{ margin: '6px 0 2px', fontSize: 11.5, color: 'var(--ew-n-500)', lineHeight: 1.45 }}>{note}</div>
      )}
      {unresolved && (
        <div style={{ margin: '8px 0 2px', padding: '8px 11px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-safety-700)', lineHeight: 1.45 }}>
          <b>No number for “{card.superName}”.</b> The name doesn't match anyone in the field directory closely enough to be sure,
          and this job has no assigned field leader to fall back on. Fix the spelling with <b>Edit</b> above, or assign the job's
          field leader on its Overview tab.
        </div>
      )}
    </>
  );
}

/**
 * Explains the mix verdict in the one place the office is already reading the
 * mix — right under the Mix / quantity row.
 *
 * Renders for `unapproved` and `ambiguous` only. Nothing for `approved` (a tick
 * on every correct card is noise) and nothing for `unchecked` (no register on the
 * job means there is nothing to be wrong about — flagging it would light up every
 * job the day this ships and train people to ignore the flag).
 */
function MixCallout({ card }: { card: PourCard }) {
  if (card.mixMatch !== 'unapproved' && card.mixMatch !== 'ambiguous') return null;
  const ambiguous = card.mixMatch === 'ambiguous';
  return (
    <div style={{ margin: '8px 0 2px', padding: '8px 11px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-safety-700)', lineHeight: 1.45 }}>
      {ambiguous ? (
        <>
          <b>Mix ID is ambiguous.</b> “{card.mix}” matches more than one entry on this job's approved list — sort out the
          duplicates on the Approved Mix Designs list.
        </>
      ) : (
        <>
          <b>Mix ID isn't on the approved list.</b> “{card.mix}” isn't among the mixes approved for this job. Either the field
          typed it wrong — fix it with <b>Edit</b> above — or the mix has been approved and needs adding to the Approved Mix
          Designs list on this tab. This doesn't stop you confirming the order.
        </>
      )}
    </div>
  );
}
